import { useForm } from "react-hook-form";
import { InputField } from "@src/shared/ui/InputField";
import { Button } from "@mui/material";
import { useGetUserInfoMe } from "@src/entities/user";
import { useUpdateUserInfo } from "@src/entities/user";
import { NavLink, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  saveBtnStyles,
  UpdateUserFormValues,
  updateUserSchema,
} from "./constants";
import { PhoneNumberFormat } from "@src/shared/ui/PhoneNumberFormat/PhoneNumberFormat";
import { useEffect } from "react";
import { useIsAuth } from "@src/entities/login/model/hooks/isAuth/useIsAuth";
import { useAuthStore } from "@src/entities/login/model/store/authStore";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { normalizeRuPhone } from "@src/shared/utils";

import styles from "./UpdateUserForm.module.scss";
import { reachGoal } from "@src/shared/lib/analytics/yandex/reachGoal";
import { YM_GOALS } from "@src/shared/lib/analytics/yandex/constants";

export const UpdateUserForm = () => {
  const { data: userInfo, refetch } = useGetUserInfoMe();
  const { mutate: updateUserInfo, isSuccess } = useUpdateUserInfo();
  const { mutate: isAuth } = useIsAuth();
  const updateAccessToken = useAuthStore((state) => state.updateAccessToken);

  const navigate = useNavigate();

  const defaultValues = {
    first_name: userInfo?.data?.first_name || "",
    last_name: userInfo?.data?.last_name || "",
    middle_name: userInfo?.data?.middle_name || "",
    email: userInfo?.data?.email || "",
    phone: normalizeRuPhone(userInfo?.data?.phone || ""),
  };

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<UpdateUserFormValues>({
    defaultValues,
    resolver: yupResolver(updateUserSchema) as any,
    mode: "onChange",
  });

  const onSubmit = (data: UpdateUserFormValues) => {
    if (!userInfo?.data?.email) return;
    const { email, phone, middle_name, ...rest } = data;
    const isNewEmail = email !== userInfo?.data?.email;
    const isNewPhone = phone !== userInfo?.data?.phone;
    const isNewMiddleName = middle_name !== userInfo?.data?.middle_name;

    const params = {
      ...(isNewEmail && { email }),
      ...(isNewPhone && { phone: phone || "" }),
      ...(isNewMiddleName && { middle_name: middle_name || "" }),
      ...rest,
    };

    updateUserInfo(
      { email: userInfo?.data?.email, data: params },
      {
        onSuccess: async (data) => {
          const userToken = data?.data?.tokens?.access;
          reachGoal(YM_GOALS.PROFILE_UPDATE);

          if (isNewEmail || isNewPhone) {
            updateAccessToken(userToken);
            await isAuth();
          }

          refetch();
        },
      },
    );
  };

  useEffect(() => {
    if (isSuccess) {
      navigate("/");
    }
  }, [isSuccess]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <InputField
        control={control}
        name="last_name"
        label="Фамилия"
        className={styles.inputField}
        classNameForWrapper={styles.inputFieldWrapper}
      />
      <InputField
        control={control}
        name="first_name"
        label="Имя"
        className={styles.inputField}
        classNameForWrapper={styles.inputFieldWrapper}
      />
      <InputField
        control={control}
        name="middle_name"
        label="Отчество"
        className={styles.inputField}
        classNameForWrapper={styles.inputFieldWrapper}
      />
      <InputField
        control={control}
        name="email"
        label="Почта"
        className={styles.inputField}
        classNameForWrapper={styles.inputFieldWrapper}
      />
      <InputField
        control={control}
        name="phone"
        label="Телефон"
        className={styles.inputField}
        classNameForWrapper={styles.inputFieldWrapper}
        slotProps={{
          input: {
            inputComponent: PhoneNumberFormat as any,
          },
        }}
      />
      <Button
        type="submit"
        variant="contained"
        disabled={!isValid}
        sx={saveBtnStyles}
      >
        Сохранить
      </Button>
      <div className={styles.links}>
        <NavLink to="/change-password" className={styles.link}>
          <StarBorderIcon className={styles.linkImg} />
          <span>Сменить пароль</span>
        </NavLink>
        <NavLink to="/account-deletion" className={styles.link}>
          <StarBorderIcon className={styles.linkImg} />
          <span>Удалить аккаунт и все данные</span>
        </NavLink>
      </div>
      <div className={styles.docsLinks}>
        <NavLink
          to="/docs/user-agreement.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          <span>Пользовательское соглашение</span>
        </NavLink>
        <NavLink
          to="/docs/privacy_policy.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          <span>Политика конфиденциальности</span>
        </NavLink>
        <NavLink
          to="/docs/consent-to-PD-processing.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          <span>Согласие на обработку персональных данных</span>
        </NavLink>
        <NavLink
          to="/docs/order-for-PD-processing.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          <span>Поручение на обработку персональных данных</span>
        </NavLink>
      </div>
    </form>
  );
};
